import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../utils/api';
import { canViewCrmTicketReports } from '../../../utils/crmPermissions';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { isMobileContainer } from '../../../utils/device';
import { Download } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import { exportTicketReportCsv } from '../../../utils/crmTicketDownload';

const REPORT_TABS = [
  { key: 'employee-performance', label: 'Employee Performance' },
  { key: 'resolution-time', label: 'Resolution Time' },
  { key: 'pending', label: 'Pending' },
  { key: 'closed', label: 'Closed' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'by-school', label: 'By School' },
  { key: 'by-category', label: 'By Category' },
];

export default function CrmTicketReports() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [active, setActive] = useState('employee-performance');
  const [data, setData] = useState(null);
  const mobile = isMobileContainer();

  useEffect(() => {
    if (!canViewCrmTicketReports(user)) return;
    api.get(`/crm/tickets/reports/${active}`).then((r) => setData(r.data?.data)).catch(() => setData(null));
  }, [active, user?.role_slug]);

  const exportCsv = async () => {
    try {
      await exportTicketReportCsv(active);
    } catch (err) {
      toast({ title: 'Export failed', description: err.message, variant: 'destructive' });
    }
  };

  if (!canViewCrmTicketReports(user)) {
    return <p className="text-muted-foreground">You do not have access to ticket reports.</p>;
  }

  const items = data?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/crm/tickets" className="text-sm text-primary hover:underline">← Back to Tickets</Link>
          <h1 className="text-2xl font-semibold mt-1">Ticket Reports</h1>
        </div>
        <Button variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-2" />Export</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {REPORT_TABS.map((t) => (
          <Button key={t.key} variant={active === t.key ? 'default' : 'outline'} size="sm" onClick={() => setActive(t.key)}>
            {t.label}
          </Button>
        ))}
      </div>

      {active === 'resolution-time' && data?.avg_hours != null && (
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Average resolution time</p>
            <p className="text-2xl font-semibold">{data.avg_hours} hours</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-border">
        <CardHeader><CardTitle>{REPORT_TABS.find((t) => t.key === active)?.label}</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No data.</p>
          ) : mobile ? (
            <div className="space-y-2">
              {items.map((row, i) => (
                <div key={i} className="border rounded p-3 text-sm space-y-1">
                  {Object.entries(row).map(([k, v]) => (
                    <div key={k}><span className="text-muted-foreground">{k}: </span>{String(v)}</div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>{Object.keys(items[0]).map((h) => <th key={h} className="text-left p-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {items.map((row, i) => (
                    <tr key={i} className="border-t">
                      {Object.values(row).map((v, j) => <td key={j} className="p-2">{String(v ?? '—')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
